<template lang="pug">
#support-list(v-cloak)
  .box
    #title.box-header.with-border.register-header.d-flex.flex-wrap
      .title お問い合わせ一覧
      .buttons.flex-grow-1
        button.btn.register-btn(@click="gotoFaqList()" type="button") よくある質問
        button.btn.register-btn(@click="gotoManualList()" type="button") マニュアル
        button.btn.register-btn(@click="gotoSupportRegister()" type="button") 新規お問い合わせ
    .box-body
      #contact-history.d-flex.flex-nowrap.align-items-stretch
        // left side list
        .contact-list.d-flex.flex-column.contact-table-component
          // テーブル
          b-table(
            :items="contactItems"
            :fields="familyFields"
            :current-page="currentPage"
            :per-page="perPage"
            :busy="isLoading"
            sticky-header
            no-border-collapse
            table-class="message-table contact-message-table"
            show-empty
          )
            // ローディング
            template(#table-busy)
              .text-center.table-loading-message
                b-spinner.spinner
                span.message.align-self-center 読込み中...
            // レコード無し
            template(#empty)
              .empty-message
                span 情報がありません

            // セル
            template(#cell(message)="row")
              .contact.d-flex.flex-wrap.align-items-stretch(@click="selectContact(row.item.caseId)" :class="{ selected : row.item.selected }")
                .information
                  .contact-type
                    span {{ convertContactType(row.item.type) }}
                  .contact-method
                    div
                      span {{ convertContactMethod(row.item.contactMethod) }}
                      span ・
                      span {{ row.item.customerName }}
                  .contact-date
                    span {{ convertCreatedDate(row.item.created) }}
                .buttons.d-flex.justify-content-end.align-items-center
                  ContactConfirmSwitch(:value="row.item.isConfirmed" @onSwitch="confirmUpdateContact(row.item)")

          .table-options.d-flex.flex-row.justify-content-center.align-items-center
            // ページあたりの行数
            span.table-option-text 1ページの行数:
            .per-page
              b-form-select(
                id="per-page-select"
                v-model="perPage"
                :options="pageOptions"
                size="md"
                @change="selectPerPage"
              )
          .table-options.d-flex.flex-row.justify-content-center.align-items-center
            // ページネーション
            .pagenation
              b-pagination.m-0(
                v-model="currentPage"
                :total-rows="rows"
                :per-page="perPage"
                :disabled="isLoading"
                align="center"
                @change="selectPage"
                hide-goto-end-buttons
              )

        // right side list
        .message-list(v-BottomScroll).d-flex.flex-column.align-items-stretch
          .message-content
            .message(v-for="message in messages")
              div(:class="messageAlign(message.senderType)")
                .text.pre-line {{ message.message }}
                .date {{ convertCreatedDate(message.created) }}
            Loading(ref="messageLoading")
          .reply-content
            .reply.d-flex.flex-wrap.align-items-stretch
              .reply-input.mr-2
                textarea.disable(
                  v-if="isConfirmed || messages.length == 0"
                  disabled
                )
                textarea(
                  v-else
                  name="reply-message"
                  v-model="replyMessage"
                  placeholder="返信内容を入力"
                  v-validate="'required|max:300'"
                  data-vv-as="返信内容"
                )
                .error-messages.flex-grow-1
                  label.error-message {{ errors.first('reply-message') }}
              .buttons.d-flex.justify-content-end.align-items-center
                button.btn.reply-btn.disable(v-if="isConfirmed || messages.length == 0" disabled) 送信
                button.btn.reply-btn(v-else @click="confirmReplyMessage()") 送信

  OkCancelModal(v-model="showUpdateModal" :title="updateMessage" @ok="updateContact")
  OkModal(v-model="showUpdateSuccessModal" :title="updateSuccessMessage")

  OkCancelModal(v-model="showSendMessageModal" title="メッセージを送信しますか？" @ok="registerReplyMessage")
  OkModal(v-model="showSendMessageSuccessModal" title="メッセージ送信に成功しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import ContactsService from "@/commons/services/Contacts";
import ContactModel from "@/commons/models/Contact";
import ContactMessagesService from "@/commons/services/ContactMessages";
import ContactMessageModel from "@/commons/models/ContactMessage";
import { SendType } from "@/commons/models/ContactMessage";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import ContactConfirmSwitch from "@/pc/basics/ContactConfirmSwitch.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import BottomScroll from "@/pc/basics/BottomScroll";
import Loading from "@/pc/basics/Loading.vue";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * お問い合わせ一覧画面コンポーネント
 */
@Component({
  components: {
    ContactConfirmSwitch,
    OkCancelModal,
    OkModal,
    PageLoading,
    Loading
  },
  directives: {
    BottomScroll
  },
  $_veeValidate: { validator: "new" }
})
export default class SupportList extends Vue {
  /** 問い合わせサービス */
  @Inject("ContactsService") contactsService!: ContactsService;
  /** 問い合わせメッセージサービス */
  @Inject("ContactMessagesService") contactMessagesService!: ContactMessagesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;
  /** 更新完了モーダルメッセージ */
  updateSuccessMessage: string = "";
  /** 返信確認モーダル表示可否 */
  showSendMessageModal: boolean = false;
  /** 返信完了モーダル表示可否 */
  showSendMessageSuccessModal: boolean = false;

  /** 1行あたりの情報配列 */
  contactItems: Array<ContactTableItem> = [];
  /** 列名配列 */
  familyFields: Array<ContactTableField> = [];
  /** 表示中の問い合わせ */
  selectedCaseId: number = 0;
  /** 問い合わせメッセージ */
  messages: Array<ContactMessageModel> = [];
  /** 表示中の問い合わせメッセージが確認済みか否か */
  isConfirmed: boolean = false;
  /** 返信メッセージ */
  replyMessage: string = "";
  /** 更新対象の問い合わせ */
  updateTargetContact: any = {};
  /** 確認済/未確認の変更モーダルメッセージは動的に設定するのでdataで保持する */
  updateMessage: string = "";

  /** 現在のページ */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページあたりの表示行数 */
  perPage: number = Number(Setting.support.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.support.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.support.pageRange;
  /** ローディング表示可否 */
  isLoading: boolean = false;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `support-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * Vue Lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<ContactTableField> = this.createFields();
    this.familyFields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.support.perPage);

    // 動的に変更する画面情報を作成する
    this.contactItems = [];
    this.currentRows = this.contactItems.length;
    this.currentPage = 1;

    await this.createView();

    return;
  }

  /**
   * ### 家族ユーザーの列名を作成する
   */
  public createFields(): Array<ContactTableField> {
    let fields: Array<ContactTableField> = [];

    fields.push({ key: "message", label: "", sortable: false, sortDirection: "desc", class: "message" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["support-list-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // ご意見テーブルに渡す各プロパティを作成する
    const contacts: Array<ContactModel> = await this.getContacts();
    const items: Array<ContactTableItem> = this.createItems(contacts);
    this.contactItems = this.contactItems.concat(items);
    this.currentRows = this.contactItems.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.contactsService.getCount(userStore, HitomeqErrorType.REQUEST_RETRY);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 1ページ分の問い合わせを作成する
   */
  public async getContacts(): Promise<Array<ContactModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc"
    };
    const response: Array<ContactModel> = await this.contactsService.get(userStore, params);

    // 完了しているか否かを設定する
    // 完了日を見て判断する
    for (let contact of response) {
      if (contact.confirmed) {
        contact.isConfirmed = true;
      } else {
        contact.isConfirmed = false;
      }
    }

    return response;
  }

  /**
   * ### 家族ユーザーの行を作成する
   */
  public createItems(contacts: Array<ContactModel>): Array<ContactTableItem> {
    let items: Array<ContactTableItem> = [];

    for (const contact of contacts) {
      items.push({
        caseId: contact.caseId ? contact.caseId : 0,
        type: contact.type,
        contactMethod: contact.contactMethod,
        customerName: contact.customerName,
        created: contact.created ? contact.created : 0,
        isConfirmed: contact.isConfirmed ? contact.isConfirmed : false,
        confirmed: contact.confirmed,
        selected: false
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.contactItems = [];
    this.currentRows = this.contactItems.length;

    this.messages = [];
    this.selectedCaseId = 0;

    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    this.messages = [];
    this.selectedCaseId = 0;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * 画面で選択したユーザーのメッセージを表示
   */
  public async selectContact(caseId: number): Promise<void> {
    const messageLoading: any = this.$refs.messageLoading;
    messageLoading.show();

    // 選択されている問い合わせを更新
    for (let i = 0; i < this.contactItems.length; i++) {
      if (this.contactItems[i].caseId == caseId) {
        this.contactItems[i].selected = true;

        // 選択された問い合わせが完了しているかを設定する
        if (this.contactItems[i].confirmed) {
          this.isConfirmed = true;
        } else {
          this.isConfirmed = false;
        }
      } else {
        this.contactItems[i].selected = false;
      }
    }

    // 問い合わせが変わるので、エラーメッセージをリセットする
    this.$validator.errors.remove("reply-message");
    // 返信内容もリセットする
    this.replyMessage = "";

    // メッセージ取得中に別の問い合わせが選択されることがありえる
    // そのため、取得したメッセージが選択中の問い合わせ以外のメッセージであれば画面更新しない
    this.selectedCaseId = caseId;
    const messages: Array<ContactMessageModel> = await this.createMessages(caseId);
    if (0 < messages.length && messages[0].caseId == this.selectedCaseId) {
      this.messages = messages;
    }
    // 画面遷移でコンポーネントが存在しないタイミングがあるため、存在可否をチェックしてからローディングを非表示にする
    if (messageLoading) {
      messageLoading.hide();
    }

    return;
  }

  /**
   * 指定したCaseIdに紐づく、画面表示用のメッセージを作成する
   *
   * @param caseId Case ID
   */
  public async createMessages(caseId: number): Promise<Array<ContactMessageModel>> {
    let messages: Array<ContactMessageModel> = await this.contactMessagesService.get(userStore, caseId);

    return messages;
  }

  /**
   * 問い合わせの完了確認ダイアログ
   */
  public confirmUpdateContact(contact: ContactTableItem): void {
    // モーダルを経由するので、更新対象の問い合わせを保持しておく
    this.updateTargetContact = contact;

    // モーダルメッセージは問い合わせの確認状態で動的に変更してから確認モーダルを表示する
    if (contact.isConfirmed) {
      this.updateMessage = "未確認に変更しますか？";
    } else {
      this.updateMessage = "確認済に変更しますか？";
    }

    this.showUpdateModal = true;

    return;
  }

  /**
   * 問い合わせの状態を完了にする
   */
  public async updateContact(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    // 問い合わせ状態の確認はconfirmedに日時が格納されることと同義なので、confirmedに日時を入れて更新する
    let contact: ContactTableItem = this.updateTargetContact;
    if (!contact.isConfirmed) {
      const date: Date = new Date();
      contact.confirmed = this.$dateUtil.convertDateToNumber(date, "yyyymmddhhMMss000");
    } else {
      // 値を消したい場合は0を渡すというAPIのIF取り決めなので0を格納
      contact.confirmed = 0;
    }
    const caseId: any = contact.caseId;
    await this.contactsService.patch(userStore, caseId, contact as any);

    // 画面情報を更新する
    contact.isConfirmed = !contact.isConfirmed;
    // 更新した問い合わせが選択中であれば、完了状態も更新する
    if (contact.caseId == this.selectedCaseId) {
      this.isConfirmed = !this.isConfirmed;
    }

    // モーダルメッセージは問い合わせの確認状態で動的に変更してから更新成功モーダルを表示する
    if (contact.isConfirmed) {
      this.updateSuccessMessage = "確認済に変更しました。";
    } else {
      this.updateSuccessMessage = "未確認に変更しました。";
    }

    this.showUpdateSuccessModal = true;

    // ヘッダーメニューの未完了バッジを更新する
    this.$emit("refresh");
    this.isBusy = false;

    return;
  }

  /**
   * 返信確認ダイアログ
   */
  public async confirmReplyMessage(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showSendMessageModal = true;
    }

    return;
  }

  /**
   * 返信内容を登録する
   */
  public async registerReplyMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "返信中...";

    // 返信内容を問い合わせメッセージとして登録する
    let message: ContactMessageModel = this.createRegisterParam();
    await this.contactMessagesService.post(userStore, message.caseId, message);

    // 登録したメッセージを追加して画面情報を更新する
    this.messages.push(message);
    // 返信内容はクリアする
    this.replyMessage = "";

    // 返信成功ダイアログを表示する
    this.showSendMessageSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * 登録用パラメータを作成する
   */
  public createRegisterParam(): ContactMessageModel {
    // 登録情報を作成する
    // messagesの0件目の情報を流用して各情報を作成する
    const date: Date = new Date();
    const crated: number = this.$dateUtil.convertDateToNumber(date, "yyyymmddhhMMss000");
    const message: ContactMessageModel = {
      caseId: this.messages[0].caseId,
      created: crated,
      facilityId: userStore.loginUser.facilityId,
      sender: userStore.loginUser.userId,
      senderName: userStore.loginUser.name,
      sendType: SendType.QUESTION,
      message: this.replyMessage,
      deleteFlag: false
    };

    return message;
  }

  /**
   * 送信種別に応じてCSS classを返す
   * * 施設の場合は左揃え
   * * 管理の場合は右揃え
   *
   * @param sender 送信ID
   */
  public messageAlign(senderType: number): string {
    if (senderType == 1) {
      return "line-left";
    } else {
      return "line-right";
    }
  }

  /**
   * お問合せの種類のコード値を文字列に変換する
   */
  public convertContactType(type: number): string {
    let convertedType: string = "";
    switch (type) {
      case 0:
        convertedType = "システムの不具合";
        break;
      case 1:
        convertedType = "クレーム・ご意見";
        break;
      case 2:
        convertedType = "改善要望";
        break;
      case 3:
        convertedType = "一般ユーザーの追加依頼";
        break;
      case 4:
        convertedType = "操作方法";
        break;
      case 5:
        convertedType = "その他ご質問";
        break;
      case 6:
        convertedType = "解約・プラン変更について";
        break;
      case 7:
        convertedType = "使用機能の変更依頼";
        break;
      default:
        convertedType = "";
        break;
    }

    return convertedType;
  }

  /**
   * 連絡方法のコード値を文字列に変換する
   */
  public convertContactMethod(contactMethod: number): string {
    let convertedContactMethod: string = "";
    switch (contactMethod) {
      case 0:
        convertedContactMethod = "システム上で回答";
        break;
      case 1:
        convertedContactMethod = "メール回答を希望";
        break;
      case 2:
        convertedContactMethod = "電話回答を希望";
        break;
      default:
        convertedContactMethod = "";
        break;
    }

    return convertedContactMethod;
  }

  /**
   * 新規投稿日時を画面表示用の文字列に変換する
   */
  public convertCreatedDate(date: number) {
    const convertedDate: string = this.$dateUtil.convertNumberToString(date, "yyyy/mm/dd hh:MM");

    return convertedDate;
  }

  /**
   * 新規お問い合わせ画面へ遷移する
   */
  public gotoSupportRegister(): void {
    this.$router.push({ name: PcUrl.SupportRegister, params: { type: "0" } });

    return;
  }

  /**
   * よくある質問(FAQ)画面へ遷移する
   */
  public gotoFaqList(): void {
    this.$router.push(PcUrl.SupportFaqList);

    return;
  }

  /**
   * マニュアル画面へ遷移する
   */
  public gotoManualList(): void {
    this.$router.push(PcUrl.SupportManualList);

    return;
  }
}

export interface ContactTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

export interface ContactTableItem {
  caseId: number;
  type: number;
  contactMethod: number;
  customerName: string;
  created: number;
  isConfirmed: boolean;
  confirmed?: number;
  selected: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#support-list {
  max-width: 1309px;
  margin-left: auto;
  margin-right: auto;

  #title {
    .title {
      // 表示用スタイル
      @include bold-font(22px);
    }
  }

  #contact-history {
    // 外枠はここで描画させる
    border: solid 1px $border-darkgray;

    // 左側、ユーザーリスト
    .contact-list {
      min-width: 40%;
      max-width: 40%;

      // 問い合わせ1行分のスタイル
      .contact {
        padding: 6px 15px;
        // リンクであることが分かるようにカーソルを変える
        cursor: pointer;
        &:hover {
          background-color: $list-hover !important;
        }

        .information {
          flex-grow: 2;
          .contact-type {
            font-weight: bold;
          }
          .contact-method {
            div {
              max-width: 320px;
              font-size: 14px;
            }
          }
          .contact-date {
            font-size: 14px;
          }
        }
        .buttons {
          max-width: 91px;
        }
      }
      // テーブルのオプション
      // マージン調整、フィルター部分のサイズを他のページより少なくする
      .table-options {
        margin-bottom: 12px;
      }
    }

    // 右側、メッセージリスト
    .message-list {
      min-width: 60%;
      max-width: 60%;
      overflow-y: auto;
      overflow-x: auto;
      border-left: solid 1px $border-darkgray;

      .message-content {
        overflow-y: scroll;
        overflow-x: auto;
        // 立幅の自動調整。画面全体の大きさからユーザーリストの高さ以外を減算する
        height: calc(100vh - 361px);
        position: relative;
        // 背景色
        background-color: $bg-message;

        .message {
          padding: 10px 0 10px 0;
          // botの発言
          .line-left {
            position: relative;
            display: inline-block;
            margin-bottom: 5px;
            clear: both;
            margin-left: 30px;
            max-width: 740px;
            // 発言本文
            .text {
              padding: 10px;
              border-radius: 20px;
              background-color: $bg-message-bot;
              margin: 0;
              margin-right: 80px;
              // LINEの吹き出しのように見せるために、CSSで図形を描画
              &::after {
                content: "";
                position: absolute;
                display: block;
                width: 0;
                height: 0;
                left: -10px;
                top: 10px;
                border-right: 20px solid $bg-message-bot;
                border-top: 10px solid transparent;
                border-bottom: 10px solid transparent;
              }
            }
            // 日付部分
            .date {
              color: $date-font-color;
              text-align: left;
              font-size: 14px;
              font-weight: bold;
            }
          }
          // 自分の発言
          .line-right {
            position: relative;
            margin-top: 5px;
            float: right;
            clear: both;
            margin-right: 30px;
            max-width: 740px;
            // 発言本文
            .text {
              padding: 10px;
              border-radius: 20px;
              background-color: $bg-message-mine;
              color: rgba(255, 255, 255, 1);
              margin: 0;
              margin-left: 80px;
              // LINEの吹き出しのように見せるために、CSSで図形を描画
              &::after {
                content: "";
                position: absolute;
                display: block;
                width: 0;
                height: 0;
                right: -10px;
                top: 10px;
                border-left: 20px solid $bg-message-mine;
                border-top: 10px solid transparent;
                border-bottom: 10px solid transparent;
              }
            }
            // 日付部分
            .date {
              color: $date-font-color;
              text-align: right;
              font-size: 14px;
              font-weight: bold;
            }
          }
        }
      }
      .reply-content {
        height: 176px;
        padding: 12px;
        .reply-input {
          flex-grow: 2;
          textarea {
            height: 126px;
            width: 100%;
            // デザインが崩れるのでリサイズは無効にする
            resize: none;
            @include font(16px);
            background-image: none;
            background-color: $bg-default;
            border: 1px solid $form-border-color;
            border-radius: 0.25rem;
            &.disable {
              cursor: not-allowed;
              background-color: $btn-disable !important;
            }
          }
        }
        // 入力欄にフォーカスが当たった際は強調する
        input:focus,
        textarea:focus,
        select:focus {
          // chromeが自動的に付与するスタイルを無効化する
          outline: none;
          box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
          border: 1px solid $form-border-select-color !important;
          border-radius: 0.25rem;
        }
        .error-messages {
          height: 27px;
        }
        .buttons {
          max-width: 76px;
          max-height: 126px;
        }
      }
    }
  }
}
</style>
